import React, { useRef } from 'react';
import { MenuOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import DropdownField from '@components/common/form/DropdownField';
import FieldSet from '@components/common/form/FieldSet';
import { beilageItemTypes as beilageItemType } from '@constants/masterData';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useTranslate from '@hooks/useTranslate';
import { CheckboxField, NumericField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Button, Col, Divider, Form, Row, Space } from 'antd';

const DraggableFoodItem = ({ field, translate, remove, id }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    return (
        <Space
            ref={setNodeRef}
            style={{ display: 'flex', alignItems: 'center', marginBottom: 8, width: '100%', ...style }}
            align="start"
            {...attributes}
        >
            <MenuOutlined style={{ touchAction: 'none', cursor: 'move' }} {...listeners} />
            <TextField name={[ field.name, 'plu' ]} placeholder={translate.formatMessage(commonMessage.plu)} className="mb-0" disabled />
            <TextField
                name={[ field.name, 'name_food' ]}
                placeholder={translate.formatMessage(commonMessage.name)}
                requiredMsg={translate.formatMessage(commonMessage.requireName)}
                required
                disabled
                className="mb-0"
            />
            <NumericField
                formatter={(v) => v}
                parser={(v) => v}
                min={0}
                name={[ field.name, 'price' ]}
                width="100%"
                placeholder={translate.formatMessage(commonMessage.price)}
                required
                className="mb-0"
            />
            <CheckboxField className="mb-0" noStyle name={[ field.name, 'required' ]} />
            <MinusCircleOutlined
                onClick={(e) => {
                    e.stopPropagation();
                    remove(field.name);
                }}
            />
        </Space>
    );
};

const BeilageForm = ({ form, formRef, handleAddMoreBeilage, openModalSelectBeilageTemplate }) => {
    const internalFormRef = useRef();
    const translate = useTranslate();
    const beilageItemTypes = translate.formatKeys(beilageItemType, [ 'label' ]);

    const onSortEnd = (fieldName) => ({ active, over }) => {
        if (active.id !== over?.id) {
            const beilages = form.current.getFieldValue('beilages') || [];
            const beilage = beilages[fieldName];
            const oldIndex = beilage.food_datas.findIndex((item) => item.id === active.id);
            const newIndex = beilage.food_datas.findIndex((item) => item.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newFoodDatas = [ ...beilage.food_datas ];
                const [ movedItem ] = newFoodDatas.splice(oldIndex, 1);
                newFoodDatas.splice(newIndex, 0, movedItem);
                beilage.food_datas = newFoodDatas.filter((el) => !!el);
                formRef.current.setFieldsValue({ beilages });
            }
        }
    };

    const renderFoodDatas = (fieldName) => (
        <Row gutter={16}>
            <Col span={24}>
                <Form.List name={[ fieldName, 'food_datas' ]}>
                    {(fields, { add, remove }) => {
                        const foodDatas = form.current?.getFieldValue([ 'beilages', fieldName, 'food_datas' ]) || [];
                        const itemsWithIds = fields.map((field, index) => ({
                            ...field,
                            id: foodDatas[index]?.id || `food-data-${field.name}-${index}`,
                        }));

                        return (
                            <DndContext onDragEnd={onSortEnd(fieldName)}>
                                <SortableContext
                                    items={itemsWithIds.map((item) => item.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="no-margin-form-item">
                                        {itemsWithIds.map((field, index) => (
                                            <DraggableFoodItem
                                                key={`item-${field.name}`}
                                                field={field}
                                                translate={translate}
                                                remove={remove}
                                                id={field.id}
                                            />
                                        ))}
                                        <Form.Item style={{ width: '174px' }}>
                                            <Button
                                                type="dashed"
                                                onClick={() => handleAddMoreBeilage(fieldName)}
                                                icon={<PlusOutlined />}
                                                block
                                            >
                                                {/* <PlusOutlined /> */}
                                                {/* {translate.formatMessage(commonMessage.addBeilageFoodItem)} */}
                                            </Button>
                                        </Form.Item>
                                    </div>
                                </SortableContext>
                            </DndContext>
                        );
                    }}
                </Form.List>
            </Col>
        </Row>
    );

    const renderBeilage = () => (
        <Form.List name="beilages">
            {(fields, { add, remove }) => (
                <div className="small-form-items" style={{ width: '100%' }}>
                    {fields.map((field, index) => (
                        <div style={{ width: '100%' }} key={index}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <TextField
                                                name={[ field.name, 'group_name' ]}
                                                placeholder={translate.formatMessage(commonMessage.beilageName)}
                                                required
                                                requiredMsg={translate.formatMessage(commonMessage.requireName)}
                                            />
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <DropdownField
                                                fieldName={[ field.name, 'type' ]}
                                                placeholder={translate.formatMessage(commonMessage.beilageType)}
                                                options={beilageItemTypes}
                                                required
                                            />
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <CheckboxField
                                                name={[ field.name, 'option' ]}
                                                optionLabel={translate.formatMessage(commonMessage.required)}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col span={15}>
                                    <FieldSet
                                        title={translate.formatMessage(commonMessage.beilageFoodData)}
                                        className="food-data-fieldset custom-fieldset"
                                    >
                                        {renderFoodDatas(field.name)}
                                    </FieldSet>
                                </Col>
                                <Col>
                                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                                </Col>
                            </Row>
                            <Divider />
                        </div>
                    ))}
                    <Form.Item style={{ width: '184px' }}>
                        <Space>
                            <Button type="dashed" onClick={() => add({ group_name: '' })} block>
                                <PlusOutlined /> {translate.formatMessage(commonMessage.addBeilageItem)}
                            </Button>
                            {openModalSelectBeilageTemplate && (
                                <Button type="dashed" onClick={openModalSelectBeilageTemplate} block>
                                    <PlusOutlined /> {translate.formatMessage(commonMessage.selectBeilageTemplate)}
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </div>
            )}
        </Form.List>
    );

    return (
        <FieldSet title={translate.formatMessage(commonMessage.beilageItems)} className="bold-fieldset custom-fieldset">
            {renderBeilage()}
        </FieldSet>
    );
};

export default BeilageForm;